import React, { useState } from 'react';

// Manual deal-metric editor — available in ANY stage to the deal owner or an org owner/admin
// ("managing member"). PUTs the changed fields to /api/deals/:id (allowlisted server-side; 403 for
// anyone else). Does not change stage (that's the StageBadge / Sold-Close flow).
// Keys must match EDIT_TEXT_COLS / EDIT_NUM_COLS in backend/routes/deals.js.
const FIELDS = [
  { key: 'address',        label: 'Address',          type: 'text',  w: '100%' },
  { key: 'city',           label: 'City',             type: 'text',  w: '48%' },
  { key: 'state',          label: 'State',            type: 'text',  w: '22%' },
  { key: 'zip',            label: 'Zip',              type: 'text',  w: '26%' },
  { key: 'beds',           label: 'Beds',             type: 'number', w: '23%' },
  { key: 'baths',          label: 'Baths',            type: 'number', w: '23%' },
  { key: 'sqft',           label: 'Sqft',             type: 'number', w: '25%' },
  { key: 'lot_size',       label: 'Lot Size',         type: 'number', w: '25%' },
  { key: 'year_built',     label: 'Year Built',       type: 'number', w: '31%' },
  { key: 'asking_price',   label: 'Asking Price',     type: 'money',  w: '33%' },
  { key: 'ai_arv',         label: 'ARV',              type: 'money',  w: '33%' },
  { key: 'repair_cost',    label: 'Rehab Estimate',   type: 'money',  w: '31%' },
  { key: 'max_offer',      label: 'Max Offer (MAO)',  type: 'money',  w: '33%', sensitive: true },
  { key: 'assignment_fee', label: 'Assignment Fee',   type: 'money',  w: '33%', sensitive: true },
  { key: 'fee',            label: 'Net Fee',          type: 'money',  w: '31%', sensitive: true },
  { key: 'sale_price',     label: 'Sold Price',       type: 'money',  w: '48%', sensitive: true },
  { key: 'close_date',     label: 'Close Date',       type: 'date',   w: '48%' },
  { key: 'title_company',  label: 'Title Company',    type: 'text',   w: '100%' },
  { key: 'buyer_name',     label: 'End Buyer',        type: 'text',   w: '100%' },
  { key: 'description',    label: 'Description / Notes', type: 'textarea', w: '100%' },
];

export default function DealEditModal({ deal, onClose, onSaved }) {
  const init = {};
  for (const f of FIELDS) {
    const v = deal?.[f.key];
    init[f.key] = v == null ? '' : (f.type === 'date' ? String(v).slice(0, 10) : String(v));
  }
  const [form, setForm] = useState(init);
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    // Only send changed keys, so we never overwrite a field the editor left untouched.
    const payload = {};
    for (const f of FIELDS) if (form[f.key] !== init[f.key]) payload[f.key] = form[f.key];
    if (!Object.keys(payload).length) { onClose(); return; }
    try {
      const r = await fetch(`/api/deals/${deal.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
      });
      const d = await r.json();
      if (!r.ok || d.error) throw new Error(d.error || 'failed');
      if (onSaved) onSaved(payload);
      onClose();
    } catch (e) { alert(`Save failed: ${e.message}`); setSaving(false); }
  }

  return (
    <div style={overlay} onClick={onClose}>
      <div style={panel} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '18px 20px', borderBottom: '1px solid var(--navy-border)' }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--white)' }}>Edit Deal</div>
            <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 3 }}>{deal?.address || ''} · editable in any stage</div>
          </div>
          <button onClick={onClose} style={closeBtn}>×</button>
        </div>

        <div style={{ padding: 20, overflowY: 'auto', flex: 1 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0 3%' }}>
            {FIELDS.map(f => (
              <div key={f.key} style={{ width: f.w, marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: f.sensitive ? '#B85450' : 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
                  {f.label}{f.sensitive ? ' ·' : ''}{f.sensitive ? <span style={{ fontWeight: 500, textTransform: 'none', letterSpacing: 0 }}> owner-only</span> : ''}
                </label>
                {f.type === 'textarea' ? (
                  <textarea value={form[f.key]} rows={3} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))} style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} />
                ) : (
                  <div style={{ position: 'relative' }}>
                    {f.type === 'money' && <span style={{ position: 'absolute', left: 12, top: 9, color: 'var(--gray-500)', fontSize: 14 }}>$</span>}
                    <input
                      type={f.type === 'money' ? 'number' : f.type}
                      value={form[f.key]}
                      onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
                      style={{ ...inputStyle, paddingLeft: f.type === 'money' ? 24 : 12 }}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div style={{ padding: '14px 20px', borderTop: '1px solid var(--navy-border)', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button onClick={onClose} style={ghostBtn}>Cancel</button>
          <button onClick={save} disabled={saving} style={{ ...primaryBtn, opacity: saving ? 0.5 : 1, cursor: saving ? 'default' : 'pointer' }}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

const overlay   = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 };
const panel     = { background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 14, width: 'min(620px, 100%)', maxHeight: '88vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.4)' };
const closeBtn  = { background: 'transparent', border: 'none', color: 'var(--gray-500)', fontSize: 24, lineHeight: 1, cursor: 'pointer', padding: 0 };
const inputStyle = { width: '100%', boxSizing: 'border-box', padding: '8px 12px', borderRadius: 8, border: '1px solid var(--navy-border)', background: 'var(--navy)', color: 'var(--white)', fontSize: 14 };
const ghostBtn   = { padding: '8px 16px', borderRadius: 8, background: 'transparent', border: '1px solid var(--navy-border)', color: 'var(--gray-300)', fontSize: 13, fontWeight: 700, cursor: 'pointer' };
const primaryBtn = { padding: '8px 18px', borderRadius: 8, background: 'var(--teal)', border: 'none', color: '#fff', fontSize: 13, fontWeight: 700 };
